import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { LungWeatherHistoryPoint, LungWeatherPattern } from '../../models/lung-weather';
import { lungWeatherColors } from './colors';
import EmptyState from '../LocationClimateViewV2/components/EmptyState';

export interface ITrackRecordProps {
  history: LungWeatherHistoryPoint[];
  patterns: LungWeatherPattern[];
  threshold: number;
}

interface ChartPoint {
  at: number;
  score: number;
  confidence: number;
  verdict: string;
  warned: boolean;
  confirmed: boolean;
}

function TrackRecordTooltip({ active, payload }: any) {
  const { t } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }

  const point: ChartPoint = payload[0].payload;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="mb-1 font-medium text-gray-700">{dayjs(point.at).format('ddd, D MMM YYYY')}</p>
      <div className="flex items-center gap-2">
        <span className="text-gray-600">{t('f-lung-weather-legend-score')}:</span>
        <span className="font-medium text-gray-900">{Math.round(point.score)}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gray-600">{t('f-lung-weather-confidence')}:</span>
        <span className="font-medium text-gray-900">{Math.round(point.confidence * 100)}%</span>
      </div>
      {point.warned && (
        <p className="mt-1 text-gray-600">
          {point.confirmed ? t('f-lung-weather-legend-confirmed') : t('f-lung-weather-legend-unconfirmed')}
        </p>
      )}
    </div>
  );
}

/**
 * Every check against the level the current sensitivity warns at, with the days treatments actually clustered
 * marked on top.
 *
 * The confusion matrix is geometry here: a marker inside the band lining up with a cluster line was a right call,
 * a cluster line with no marker before it was missed, and a marker with no cluster after it was a false alarm. No
 * extra hue is needed for any of that, which is deliberate - green for "the warning came true" would be
 * celebrating an outbreak.
 */
const TrackRecord: React.FC<ITrackRecordProps> = ({ history, patterns, threshold }) => {
  const { t } = useTranslation();

  const data: ChartPoint[] = React.useMemo(
    () =>
      history.map((point) => ({
        at: new Date(point.checkedAt).getTime(),
        score: point.matchScore,
        confidence: point.confidence,
        verdict: point.verdict,
        warned: point.matchScore >= threshold,
        confirmed: Boolean(point.confirmedAt)
      })),
    [history, threshold]
  );

  // One check is a dot, not a trend. Saying so beats drawing an axis whose two ends carry the same date.
  if (data.length < 2) {
    return (
      <EmptyState
        title={t('f-lung-weather-history-thin-title')}
        description={t('f-lung-weather-history-thin', { count: data.length })}
      />
    );
  }

  const first = data[0].at;
  const last = data[data.length - 1].at;

  const peaks = patterns
    .map((pattern) => new Date(pattern.detectedAt).getTime())
    .filter((at) => at >= first && at <= last);

  return (
    <div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={lungWeatherColors.grid} />

          {/* The band a match has to reach to warn under the current sensitivity. */}
          <ReferenceArea y1={threshold} y2={100} fill={lungWeatherColors.warningSoft} stroke="none" />
          <ReferenceLine
            y={threshold}
            stroke={lungWeatherColors.warning}
            strokeDasharray="4 3"
            label={{
              value: t('f-lung-weather-warns-at', { value: Math.round(threshold) }),
              position: 'insideTopRight',
              fill: lungWeatherColors.warning,
              fontSize: 11
            }}
          />

          {peaks.map((at) => (
            <ReferenceLine key={at} x={at} stroke={lungWeatherColors.ink} strokeDasharray="2 2" />
          ))}

          <XAxis
            dataKey="at"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(value: number) => dayjs(value).format('D MMM')}
            tick={{ fontSize: 11, fill: lungWeatherColors.muted }}
            axisLine={false}
            tickLine={false}
            minTickGap={32}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 11, fill: lungWeatherColors.muted }}
            axisLine={false}
            tickLine={false}
            width={40}
          />
          <Tooltip content={<TrackRecordTooltip />} />

          <Line
            type="monotone"
            dataKey="score"
            stroke={lungWeatherColors.accent}
            strokeWidth={2}
            // A dot on every day would be noise; only the days that crossed the line get one, filled when a real
            // peak followed and hollow when nothing did.
            dot={(props: any) => {
              const point: ChartPoint = props.payload;

              if (!point.warned) {
                return <g key={props.key} />;
              }

              return (
                <circle
                  key={props.key}
                  cx={props.cx}
                  cy={props.cy}
                  r={4.5}
                  fill={point.confirmed ? lungWeatherColors.accent : lungWeatherColors.surface}
                  stroke={lungWeatherColors.accent}
                  strokeWidth={2}
                />
              );
            }}
            activeDot={{ r: 5, fill: lungWeatherColors.accent, stroke: lungWeatherColors.surface, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
        <Legend label={t('f-lung-weather-legend-score')} kind="line" />
        <Legend label={t('f-lung-weather-legend-confirmed')} kind="filled" />
        <Legend label={t('f-lung-weather-legend-unconfirmed')} kind="hollow" />
        <Legend label={t('f-lung-weather-legend-cluster')} kind="tick" />
      </div>
    </div>
  );
};

const Legend: React.FC<{ label: string; kind: 'line' | 'filled' | 'hollow' | 'tick' }> = ({ label, kind }) => (
  <span className="flex items-center gap-2 text-xs text-gray-600">
    <svg width={20} height={14} aria-hidden="true">
      {kind === 'line' && <line x1={0} y1={7} x2={20} y2={7} stroke={lungWeatherColors.accent} strokeWidth={2} />}
      {kind === 'tick' && (
        <line x1={10} y1={1} x2={10} y2={13} stroke={lungWeatherColors.ink} strokeWidth={2} strokeDasharray="2 2" />
      )}
      {(kind === 'filled' || kind === 'hollow') && (
        <circle
          cx={10}
          cy={7}
          r={4.5}
          fill={kind === 'filled' ? lungWeatherColors.accent : lungWeatherColors.surface}
          stroke={lungWeatherColors.accent}
          strokeWidth={2}
        />
      )}
    </svg>
    {label}
  </span>
);

export default TrackRecord;
